/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component, OnInit, Input, Output, EventEmitter, OnChanges, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { ISelectable } from 'starbase-launcher';
import { Observable, from, of } from 'rxjs';

/** Selection style for `SidebarFilterComponent` inner options */
export enum SidebarFilterTypes {
  /** Radio buttons -- mutually exclusive choices */
  RadioButtonMode = 1,
  /** Checkboxes -- select multiple */
  CheckboxMode = 2,
  /** Dropdown -- select one from long list */
  SelectDropdownMode = 3
}

export interface IFilterEventData {
  /** Whether or not the value for the chosen filter is on or off */
  selected: boolean;
  /**  The name of the entity chosen for filtering */
  entity: string;
  /** The object or value that was chosen ('All' for reset this filter group) */
  obj: any;
  /** Observable response of polling all optionData for selected == true */
  allSelected: Observable<string>;
}

/** Individual filter component for displaying on the filter/search sidebar of the gallery. */
@Component({
  selector: 'app-sidebar-filter',
  templateUrl: './sidebar-filter.component.html',
  styleUrls: ['./sidebar-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarFilterComponent implements OnInit, OnChanges {
  @Input() filterTitle = '';
  @Input() optionData: ISelectable[];
  @Input() filterBindingProperty = '';
  @Input() filterDisplayProperty = '';
  @Input() filterTooltipProperty = '';
  @Input() displayPropertyIsFunc = false;
  /** @default CheckboxMode */
  @Input() displayMode: SidebarFilterTypes = SidebarFilterTypes.CheckboxMode;
  /** Set to true to show the 'filter reduction' text box.
   *
   * @default false */
  @Input() showMetaFilter = false;
  /** Adds the "All X" option to the top of the list of filters */
  @Input() includeAllOption = true;
  /** Expand the filter if true, collapse if false */
  @Input() open = false;
  @Input() openerProp: string;
  /** The pixel width of the parent container to propagate into CDK scroll viewport.
   *
   * @default 245 */
  @Input() parentWidth = 245;
  @Input() maxHeightOverwrite: number;

  @Output() filterChangeEmitter: EventEmitter<IFilterEventData> = new EventEmitter<IFilterEventData>();
  @Output() toggleOpen = new EventEmitter<string>();

  @ViewChild('metaFilterTxt', { static: true }) metaFilterTxtInput: ElementRef;

  public allOptionIsSelected = false;
  public radioValStorage = '';
  public metaFilter = '';
  public titleHover = false;

  defaultInnerFiltersHeight = 160; // px

  constructor(private cd: ChangeDetectorRef) { }

  public static pollSelectedOnFilterObjects(optionData: ISelectable[], bindingProperty: string): Observable<string> {
    return from(optionData.filter(o => o.selected).map(oo => oo[bindingProperty].toString()));
  }
  public static resetSelectedInArray(arr: ISelectable[], set: boolean = false): void {
    arr.forEach(a => a.selected = set);
  }


  ngOnInit() {
    if (this.filterTitle === '' || this.optionData === undefined || !this.optionData.length || this.filterDisplayProperty === '') {
      console.error(`A Sidebar Filter component, ${this.filterTitle ?? 'Unnamed'}, is not set up properly.`);
    }
    if (this.optionData.length > 30) {
       // disable 'All X' if there are too many options. This prevents truncation of the filter on the back-end
      this.includeAllOption = false;
    }
    if (this.displayMode === SidebarFilterTypes.RadioButtonMode) {
      if (this.optionData.filter(a => a.selected).length === 1 && this.optionData[0].selected === true) {
 this.allOptionIsSelected = true;
}
      const findIt = this.optionData.find(a => a.selected);
      if (findIt) {
        this.radioValStorage = findIt[this.filterBindingProperty];
      }
    }
    this.cd.markForCheck();
  }
  ngOnChanges() {
    try {
      if (this.displayPropertyIsFunc) {
        this.optionData.forEach(o => {
          o[this.filterDisplayProperty] = o[this.filterDisplayProperty]();
        });
      }
    } catch {
      // ignore the error
    } finally {
      this.cd.markForCheck();
    }
  }

  countOfSelected(): Observable<number> {
    if (this.displayMode === SidebarFilterTypes.RadioButtonMode) {
      const nothing = this.allOptionIsSelected
        || (this.optionData[0] && this.optionData[0].selected) || this.optionData.filter(a => a.selected).length === 0;
      return of(nothing ? 0 : 1);
    }
    return of(this.optionData.filter(o => o.selected).length);
  }

  public emitFilterChange(selected: boolean, obj: ISelectable | string): void {
    this.allOptionIsSelected = false;
    if (this.displayMode === SidebarFilterTypes.RadioButtonMode) {
      this.optionData.forEach(a => a.selected = false);
    }
    if (typeof (selected) === 'boolean') {
      setTimeout(() => {
        try {
          obj['selected'] = selected;
        } catch {
           // radio buttons?
         } // will not work with string
      });
    } else { // string
      setTimeout(() => {
        try {
          obj['selected'] = !obj['selected'];
          selected = obj['selected'];
        } catch { // will hit catch with string ('all')
          selected = false; // default ?
        }
      });
    }
    if (obj.toString().toLowerCase() === 'all' || (obj['disp'] && obj['disp'].toString().toLowerCase() === 'all')) {
      if (typeof (selected) === 'boolean') {
        this.allOptionIsSelected = selected;
      } else {
        this.allOptionIsSelected = !this.allOptionIsSelected;
      }
      this.setAllOptionsSelected(this.allOptionIsSelected);
    }

    if (this.displayMode === SidebarFilterTypes.SelectDropdownMode) {
      this.filterChangeEmitter.emit(
        {
          selected,
          entity: this.filterTitle,
          obj,
          allSelected: SidebarFilterComponent.pollSelectedOnFilterObjects(this.optionData, this.filterBindingProperty)
        } as IFilterEventData);
    } else {
      this.filterChangeEmitter.emit(
        {
          selected,
          entity: this.filterTitle,
          obj: obj[this.filterBindingProperty],
          allSelected: SidebarFilterComponent.pollSelectedOnFilterObjects(this.optionData, this.filterBindingProperty)
        } as IFilterEventData);
    }
  }

  public resetFilter(): void {
    this.optionData.forEach(a => a.selected = false);
    if (this.includeAllOption && (this.displayMode !== SidebarFilterTypes.CheckboxMode)) { // not checkboxes
      this.allOptionIsSelected = true;
    }
    if (this.displayMode === SidebarFilterTypes.RadioButtonMode) { // only radio buttons
      try {
        if ('val' in this.optionData[0]) {
          this.optionData[0].selected = true;
          this.radioValStorage = 'All';
        }
      } catch { }
    }
    this.metaFilter = '';
    this.cd.markForCheck();
  }
  public collapse() {
    this.open = false;
    this.cd.markForCheck();
  }

  private setAllOptionsSelected(selected: boolean): void {
    if (this.displayMode === SidebarFilterTypes.CheckboxMode) {
      this.optionData.forEach(a => a.selected = selected);
    }
  }

}
